import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgSelectModule } from "@ng-select/ng-select";
import { Configuration } from "@app/app.constants";
import { NotificationModel } from "@app/mailmessages/models/notificationModel";
import { NotificationService } from "@app/mailmessages/service/notification.service";
import { CerrixEditorComponent } from "@app/shared/components/cerrix-editor/cerrix-editor.component";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";
import { CerrixSelectTreeComponent } from "@app/shared/cerrix-select-tree/cerrix-select-tree.component";
import { CerrixTextfieldComponent } from "@app/shared/components/cerrix-textinput/cerrix-textfield/cerrix-textfield.component";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { getFormControl } from "@methods/CommonMethods";
import { isEmpty } from "@methods/isEmpty";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { TabService } from "@services/tabs/TabService";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/mailmessages/i18n/scope";
import { ToastrService } from "ngx-toastr";
import { Observable, firstValueFrom } from "rxjs";
import { finalize } from "rxjs/operators";

@Component({
    selector: "uc-notification-detail",
    templateUrl: "./notification-detail.component.html",
    styleUrls: ["./notification-detail.component.scss"],
    imports: [
        ReactiveFormsModule,
        MatTooltipModule,
        NgSelectModule,
        CheckboxComponent,
        CerrixSelectTreeComponent,
        CerrixTextfieldComponent,
        CerrixEditorComponent,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)]
})
export class NotificationDetailComponent implements OnInit {
    private _mmService = inject(NotificationService);
    private _route = inject(ActivatedRoute);
    private toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @ViewChild("editor") editorComponent: CerrixEditorComponent;

    tab: TabModel;
    id: number;

    pageReady = false;
    module: ModuleType;
    isTaskModule: boolean = false;
    enableTypeField: boolean = false;

    notification: NotificationModel;
    keywords: string[];
    orgs: CerrixTreeItem[];
    mailEnabled: boolean;

    periods: IdNameCombination[] = [];
    types: IdNameCombination[] = [];
    subTypes: IdNameCombination[] = [];
    recipients: any[] = [];

    form: FormGroup;
    isEmpty = false;
    organizationRequest: Observable<CerrixTreeItem[]>;
    noOrganizationsSelectedDescription = Configuration.NoOrganizationsSelectedDescription;

    subjectHasFocus: boolean;

    constructor() {
        this.id = this._route.snapshot.params.id;
    }

    async ngOnInit() {
        await this._translation.ready();

        const config = this.tab.config;
        if (!config) {
            this.toastr.error(
                this._translation.t("notificationDetail.errorOpeningDetailsMessage")
            );
            this.tab.close(false);
            return;
        }
        this.module = config.module;
        this.isTaskModule = this.module === ModuleType.Tasks;
        this.enableTypeField = config.module === ModuleType.BIM || config.module === ModuleType.IM;

        this._mmService.getNotification(this.id, this.module).subscribe((notification) => {
            if (this.id === 0) {
                notification.period = null;
                notification.subType = null;
                if (this.isTaskModule) {
                    // pbi=12764: only here default changes .....
                    notification.active = true;
                    notification.subject = 'Notification for task " [TaskName] "';
                }
            }
            this.keywords = notification.keywords;
            this.notification = notification;
            this.mailEnabled = this.notification.active;

            this._mmService.getNotificationStandingData(this.module).subscribe((data) => {
                this.periods = data.periods;
                this.types = data.types;
                this.subTypes = data.subTypes;
                this.reloadRecipients(notification.subType);
                this.initFormGroup(notification);
                this.pageReady = true;
            });
        });
        this.organizationRequest = this._mmService.getOrganizations();
    }

    initFormGroup(notification: NotificationModel) {
        this.form = new FormGroup({
            name: new FormControl(notification.name, [Validators.required]),
            active: new FormControl(notification.active),
            recipients: new FormControl(notification.recipients),
            subject: new FormControl(notification.subject, [Validators.required]),
            organizations: new FormControl(notification.organizations),
        });

        if (this.enableTypeField)
            this.form.addControl("types", new FormControl(notification.types, Validators.required));

        if (!this.isTaskModule) {
            this.form.addControl(
                "period",
                new FormControl(notification.period, Validators.required)
            );
            this.form.addControl(
                "subType",
                new FormControl(notification.subType, Validators.required)
            );
        }

        this.form.valueChanges.subscribe(() =>
            FormValidationHelper.mapToModel(this.form, this.notification)
        );
    }

    onChange(message: string) {
        this.notification.message = message;
    }

    addKeyword(keyword: string) {
        if (this.subjectHasFocus) {
            const ctrl = getFormControl(this.form, "subject");
            const value = ctrl.value;
            ctrl.setValue(value + " [" + keyword + "]");
        } else {
            this.editorComponent.insertText(" [" + keyword + "]");
        }
    }

    reloadRecipients(item: IdNameCombination | number) {
        if (!item) {
            return;
        }

        let id = 0;
        if (typeof item === "number") {
            id = item;
        } else {
            id = item.ID;
        }

        this._mmService
            .getRecipients(this.module, id, this.notification.recipients)
            .subscribe((users) => {
                this.recipients = users;
            });
    }

    async save() {
        this.isEmpty = false;
        if (!this.notification.organizations) {
            this.notification.organizations = [];
        }

        const messageEmpty = this.editorComponent.isEmpty();

        if (this.form.valid && !messageEmpty) {
            this.notification.id = this.id;

            const savingPrompt = this._promptService.loader(
                this._translation.t("notificationDetail.savingChangesMessage")
            );
            await firstValueFrom(this._mmService
                .saveNotification(this.notification)
                .pipe(finalize(() => savingPrompt.close())))
                .then((model: NotificationModel) => {
                    this.id = model.id;
                    this._tabService.refresh(this._pages.MailMessages);

                    this.toastr.success(
                        "",
                        this._translation.t("notificationDetail.updateCompletedTitle")
                    );
                })
                .catch((_) => {
                    this.toastr.error(
                        "",
                        this._translation.t("notificationDetail.updateFailedTitle")
                    );
                });
        } else {
            FormValidationHelper.markAllAsTouched(this.form);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.form);
            let validationMessage = FormValidationHelper.getGeneralErrorMessage(validationErrors);

            if (messageEmpty) {
                this.isEmpty = true;
                validationMessage += `<br> - ${this._translation.t("notificationDetail.messageRequiredError")}`;
            }

            this.toastr.warning(
                validationMessage,
                this._translation.t("notificationDetail.saveFailedTitle"),
                { enableHtml: true }
            );
        }
    }

    deleteClick() {
        this._mmService.deleteNotification(this.id).subscribe((_) => {
            this._tabService.refresh(this._pages.MailMessages);
            this.toastr.success(
                this._translation.t("notificationDetail.notificationDeletedMessage")
            );
            this.tab.close(false);
        });
    }

    validate() {
        let isValid = true;
        const errors: string[] = [];
        if (isEmpty(this.notification.name)) {
            errors.push(this._translation.t("notificationDetail.nameRequiredError"));
            isValid = false;
        }

        if (isEmpty(this.notification.subject)) {
            errors.push(this._translation.t("notificationDetail.subjectRequiredError"));
            isValid = false;
        }

        if (errors.any()) {
            this.toastr.warning(
                errors.join("<br />"),
                this._translation.t("notificationDetail.validationsTitle"),
                { enableHtml: true }
            );
        }

        return isValid;
    }

    setFocusToSubject() {
        this.subjectHasFocus = true;
    }

    deFocusSubject() {
        this.subjectHasFocus = false;
    }
}
